import { lazy } from "react";
import { FormattedMessage } from "react-intl";
import { PermissionNames } from "~enums/permissions.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import type { TabRoute } from "../types/TabRoute.ts";

const SeasonPoolListPage = lazy(() => import("~Features/SeasonPools/SeasonPools"));
const SeasonEditPage = lazy(() => import("~Features/EditSeason/EditSeason"));
const SeasonStageListPage = lazy(() => import("~Features/SeasonStages/SeasonStages"));
const SeasonEntityListPage = lazy(() => import("~Features/SeasonTeams/SeasonTeams"));
const SeasonSeriesListPage = lazy(() => import("~Features/SeriesTab/SeriesTab"));
const SeasonSiteListPage = lazy(() => import("~Features/Sites/SeasonSites"));
const SeasonVenueListPage = lazy(() => import("~Features/Venues/Season/SeasonVenues"));
const SeasonEntityImagesPage = lazy(() => import("~Features/SeasonTeamsImages/SeasonTeamImages"));
const SeasonStandingsPage = lazy(() => import("~pages/Standings/StandingsPage"));
const SeasonFinalStandingsPage = lazy(() => import("~pages/FinalStandings/FinalStandingsPage"));
const SeasonEntityStatsPage = lazy(() => import("~Features/SeasonTeamStats/SeasonTeamStats"));
const FixturesListPage = lazy(() => import("~pages/Fixtures/FixtureListPage"));
const SeasonReportsPage = lazy(() => import("~pages/Reports/SeasonReportsPage"));
const SeasonOfficialsPage = lazy(() => import("~pages/Officials/SeasonOfficialsPage"));
const AssignFixtureOfficialsPage = lazy(() => import("~pages/Officials/AssignFixtureOfficialsPage"));

const useGetSeasonTabRoutes = (): TabRoute[] => {
  const { seasonEntityPermissionName, fixturePermissionName } = usePermissionsByCompetitionName();

  return [
    {
      key: "fixtures",
      path: "fixtures",
      label: <FormattedMessage id="season.tabs.fixtures" />,
      Component: FixturesListPage,
      permissionKey: fixturePermissionName,
    },
    {
      key: "stages",
      path: "stages",
      label: <FormattedMessage id="season.tabs.stages" />,
      Component: SeasonStageListPage,
      permissionKey: PermissionNames.STAGE,
    },
    {
      key: "pools",
      path: "pools",
      label: <FormattedMessage id="season.tabs.pools" />,
      Component: SeasonPoolListPage,
      permissionKey: PermissionNames.POOL,
    },
    {
      key: "series",
      path: "series",
      label: <FormattedMessage id="season.tabs.series" />,
      Component: SeasonSeriesListPage,
      permissionKey: PermissionNames.SEASON_SERIES,
    },
    {
      key: "entities",
      path: "entities",
      label: <FormattedMessage id="season.tabs.entities" />,
      Component: SeasonEntityListPage,
      permissionKey: seasonEntityPermissionName,
    },
    {
      key: "entity-images",
      path: "entity-images",
      label: <FormattedMessage id="season.tabs.entity.images" />,
      Component: SeasonEntityImagesPage,
      permissionKey: seasonEntityPermissionName,
    },
    {
      key: "entity-statistic",
      path: "entity-statistic",
      label: <FormattedMessage id="season.tabs.entity.stats" />,
      Component: SeasonEntityStatsPage,
      permissionKey: PermissionNames.SEASON_STATISTIC,
    },
    {
      key: "officials",
      path: "officials",
      label: <FormattedMessage id="season.tabs.seasonOfficials" />,
      Component: SeasonOfficialsPage,
      permissionKey: PermissionNames.ROLES,
    },
    {
      key: "assign-officials",
      path: "assign-officials",
      label: <FormattedMessage id="season.tabs.assignOfficials" />,
      Component: AssignFixtureOfficialsPage,
      permissionKey: PermissionNames.ROLES,
    },
    {
      key: "sites",
      path: "sites",
      label: <FormattedMessage id="season.tabs.sites" />,
      Component: SeasonSiteListPage,
      permissionKey: PermissionNames.SITE,
    },
    {
      key: "venues",
      path: "venues",
      label: <FormattedMessage id="season.tabs.venues" />,
      Component: SeasonVenueListPage,
      permissionKey: PermissionNames.VENUE,
    },
    {
      key: "standings",
      path: "standings",
      label: <FormattedMessage id="season.tabs.standings" />,
      Component: SeasonStandingsPage,
      permissionKey: PermissionNames.STANDING,
    },
    {
      key: "final-standings",
      path: "final-standings",
      label: <FormattedMessage id="season.tabs.finalStandings" />,
      Component: SeasonFinalStandingsPage,
      permissionKey: PermissionNames.STANDING,
    },
    {
      key: "reports",
      path: "reports",
      label: <FormattedMessage id="season.tabs.reports" />,
      Component: SeasonReportsPage,
      permissionKey: PermissionNames.REPORT,
    },
    {
      key: "details",
      path: "details",
      label: <FormattedMessage id="season.tabs.details" />,
      Component: SeasonEditPage,
      permissionKey: PermissionNames.SEASON,
    },
  ];
};

export default useGetSeasonTabRoutes;
